// src/array.ts
var findFirst = (arr) => arr[0];
var findLast = (arr) => arr[arr.length - 1];
var findWithProp = (items, prop, value) => items.find((item) => item[prop] === value);
var getFirst = (arr) => {
  if (arr.length === 0) throw new Error(`Array is empty`);
  return arr[0];
};
var getLast = (arr) => {
  if (arr.length === 0) throw new Error(`Array is empty`);
  return arr[arr.length - 1];
};
var insert = (items, index, item) => {
  const before = items.slice(0, index);
  const after = items.slice(index);
  return [...before, item, ...after];
};
var remove = (items, index) => {
  const before = items.slice(0, index);
  const after = items.slice(index + 1);
  return [...before, ...after];
};
var includes = (array, item) => array.includes(item);
var compact = (array) => array.filter((item) => Boolean(item));
var includesSome = (array, items) => items.some((item) => includes(array, item));

// src/api.ts
var convertOneOfLikeDictToUnion = (item) => getFirst(Object.values(item));

// src/document.ts
var getBaseUrl = () => {
  var _a, _b, _c;
  return (_c = (_b = (_a = document.querySelector("base")) == null ? void 0 : _a.getAttribute("href")) == null ? void 0 : _b.slice(0, -1)) != null ? _c : "";
};

// src/function.ts
var noop = () => {
};
var identity = (value) => value;

// src/object.ts
import deepmerge from "deepmerge";
import isobject from "isobject";
import set from "lodash/fp/set";
import getOr from "lodash/fp/getOr";
import omit from "lodash/fp/omit";
import { default as default2 } from "fast-deep-equal";
var mergeDeep = (left, right) => deepmerge(left, right);
var isObject = (value) => isobject(value);
var clone = (value) => deepmerge({}, value);
var assocPath = (breadcrumbs, value, object) => set(breadcrumbs, value, object);
var pathOr = (defaultValue, breadcrumbs, object) => breadcrumbs.length ? getOr(defaultValue, breadcrumbs, object) : object;
var path = (breadcrumbs, object) => pathOr(void 0, breadcrumbs, object);
var assoc = (prop, value, object) => object ? assocPath([prop], value, object) : void 0;
var dissoc = (prop, object) => {
  if (!object) return void 0;
  return omit([prop], object);
};

// src/ssi.ts
import { isBrowser, isNode } from "browser-or-node";
var createSsiValue = (varName) => `<!--#echo var='${varName}'-->`;
var ENV_VAR_NAME = "UI_PROJECT_VALUES";
var SSI_VALUE = createSsiValue(ENV_VAR_NAME);
var DATA_ATTRIBUTE = "data-ui-project-values";
var BUNDLE_BASE_URL_ENV_VAR_NAME = "UI_BUNDLE_BASE_URL";
var BUNDLE_BASE_URL_SSI_VALUE = createSsiValue(BUNDLE_BASE_URL_ENV_VAR_NAME);
var getSsiValue = (varName) => {
  var _a, _b;
  let varValue = isNode ? (_a = process.env[varName]) != null ? _a : process.env[`REACT_APP_${varName}`] : void 0;
  if (!varValue && isBrowser) {
    const dataVarName = `data-${varName.replace(/_/g, "-").toLowerCase()}`;
    varValue = (_b = window.document.documentElement.getAttribute(dataVarName)) != null ? _b : void 0;
    if (varValue && /^<!--\s*#\s*echo\s+var=/.test(varValue) || varValue === "(none)") {
      varValue = void 0;
    }
  }
  return varValue;
};
var getSsiJsonValue = (varName) => {
  const varStringifiedValue = getSsiValue(varName);
  let varValue = void 0;
  try {
    if (varStringifiedValue) {
      varValue = JSON.parse(varStringifiedValue);
    }
  } catch (e) {
  }
  return varValue;
};
var getSsiJsonValueObject = (varName) => {
  const varValue = getSsiJsonValue(varName);
  return typeof varValue === "object" ? varValue : void 0;
};
var getProjectValues = () => {
  var _a;
  return (_a = getSsiJsonValueObject(ENV_VAR_NAME)) != null ? _a : {};
};

// src/string.ts
var toUpperCamelCase = (str) => {
  let inputStr = str;
  let result = "";
  let capNext = true;
  if (inputStr.toUpperCase() === str) {
    inputStr = str.toLowerCase();
  }
  for (let index = 0; index < inputStr.length; index++) {
    const char = inputStr[index];
    if (char === "_" || char === " " || char === "-") {
      capNext = true;
    } else {
      if (capNext) {
        result += char.toUpperCase();
      } else {
        result += char;
      }
      capNext = !isNaN(Number(char));
    }
  }
  return result;
};
var unTitle = (str) => str.replace(/^(.)/, (char) => char.toLowerCase());

// src/types.ts
var isNotNil = (item) => item != null;
var isCtorInstance = (ctor, value) => value instanceof ctor;
function assertCtorInstance(ctor, value) {
  if (isCtorInstance(ctor, value)) return;
  throw new TypeError(`Can't assert ${value} as instance of ${ctor}`);
}

// src/binary.ts
import { isBrowser as isBrowser2, isNode as isNode2 } from "browser-or-node";
var fromBinaryToString = (value) => {
  if (isBrowser2) return window.atob(value);
  if (isNode2) return Buffer.from(value, "base64").toString();
  throw new Error(`Unsupported environment`);
};
var fromStringToBinary = (value) => {
  if (isBrowser2) return window.btoa(value);
  if (isNode2) return Buffer.from(value, "utf8").toString("base64");
  throw new Error(`Unsupported environment`);
};
var fromBinaryToObject = (value) => JSON.parse(fromBinaryToString(value));
var fromObjectToBinary = (value) => fromStringToBinary(JSON.stringify(value));
var fromBinaryToFile = (fileBody, fileName, fileType) => {
  const fileBodyAsciiString = fromBinaryToString(fileBody);
  let n = fileBodyAsciiString.length;
  const fileBodyBinaryArr = new Uint8Array(n);
  while (n--) {
    fileBodyBinaryArr[n] = fileBodyAsciiString.charCodeAt(n);
  }
  return new File([fileBodyBinaryArr], fileName, {
    type: fileType
  });
};

// src/traversals.ts
var pathSplitRegexp = /\]?\.|\[|\]/;
var toPath = (path2) => path2.trim().split(pathSplitRegexp).filter(Boolean).map((token) => isNaN(Number(token)) ? token : Number(token));
export {
  BUNDLE_BASE_URL_ENV_VAR_NAME,
  BUNDLE_BASE_URL_SSI_VALUE,
  DATA_ATTRIBUTE,
  ENV_VAR_NAME,
  SSI_VALUE,
  assertCtorInstance,
  assoc,
  assocPath,
  clone,
  compact,
  convertOneOfLikeDictToUnion,
  dissoc,
  findFirst,
  findLast,
  findWithProp,
  fromBinaryToFile,
  fromBinaryToObject,
  fromBinaryToString,
  fromObjectToBinary,
  fromStringToBinary,
  getBaseUrl,
  getFirst,
  getLast,
  getProjectValues,
  getSsiJsonValue,
  getSsiJsonValueObject,
  getSsiValue,
  identity,
  includes,
  includesSome,
  insert,
  isCtorInstance,
  default2 as isEqual,
  isNotNil,
  isObject,
  mergeDeep,
  noop,
  omit,
  path,
  pathOr,
  remove,
  toPath,
  toUpperCamelCase,
  unTitle
};
//# sourceMappingURL=index.mjs.map